import { sanitizeNullStrings } from "@/utils/sanitize";
import { useEffect, useMemo, useState } from "react";
import {
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Divider,
	FormControlLabel,
	Grid2,
	IconButton,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import type { ProductType } from "../models/ProductType";

interface FormErrors {
	Name?: string;
}

interface ProductTypeDetailViewerProps {
	productType: ProductType | null;
	existingProductTypes: ProductType[];
	isSaving: boolean;
	onSave: (productType: ProductType) => void;
	onClose: () => void;
}

export const ProductTypeDetailViewer = ({
	productType,
	existingProductTypes,
	isSaving,
	onSave,
	onClose,
}: ProductTypeDetailViewerProps) => {
	const [formData, setFormData] = useState<ProductType | null>(null);
	const [errors, setErrors] = useState<FormErrors>({});
	const [touched, setTouched] = useState<Record<string, boolean>>({});

	const isNewProductType = !productType?.Id;

	const validateForm = useMemo(() => {
		return (data: ProductType | null): FormErrors => {
			const newErrors: FormErrors = {};

			if (!data?.Name || data.Name.trim().length === 0) {
				newErrors.Name = "Name is required";
			} else {
				const isDuplicate = existingProductTypes.some(
					(pt) =>
						pt.Id !== data.Id &&
						pt.Name.toLowerCase() === data.Name.trim().toLowerCase(),
				);
				if (isDuplicate) {
					newErrors.Name = "This name already exists";
				}
			}

			return newErrors;
		};
	}, [existingProductTypes]);

	const formErrors = useMemo(
		() => validateForm(formData),
		[formData, validateForm],
	);
	const isFormValid = Object.keys(formErrors).length === 0;
	const canCreate = isNewProductType && isFormValid;

	useEffect(() => {
		if (productType) {
			setFormData(sanitizeNullStrings({ ...productType }));
		} else {
			setFormData(null);
		}
		setErrors({});
		setTouched({});
	}, [productType]);

	const handleChange = (field: keyof ProductType, value: unknown) => {
		setFormData((prev) => (prev ? { ...prev, [field]: value } : null));
	};

	const handleBlur = (field: keyof ProductType) => {
		setTouched((prev) => ({ ...prev, [field]: true }));
		if (formData) {
			setErrors(validateForm(formData));
		}
	};

	const handleBlurSave = (field: keyof ProductType, value: unknown) => {
		handleBlur(field);

		if (isNewProductType) return;

		const updatedData = formData ? { ...formData, [field]: value } : null;
		const currentErrors = validateForm(updatedData);
		if (currentErrors[field as keyof FormErrors]) return;

		const originalValue = productType?.[field];
		const normalizedOriginal = originalValue === null ? "" : originalValue;
		const normalizedNew = value === null ? "" : value;

		if (normalizedOriginal !== normalizedNew) {
			onSave({ ...formData!, [field]: value });
		}
	};

	const handleNumericBlurSave = (
		field: keyof ProductType,
		rawValue: string,
	) => {
		const value = rawValue === "" ? null : Number.parseFloat(rawValue);
		handleChange(field, value);
		handleBlurSave(field, value);
	};

	const handleCheckboxChange = (field: keyof ProductType, value: unknown) => {
		handleChange(field, value);

		if (isNewProductType) return;

		if (productType?.[field] !== value) {
			onSave({ ...formData!, [field]: value });
		}
	};

	const handleCreate = () => {
		setTouched({ Name: true });
		setErrors(formErrors);

		if (formData && canCreate) {
			onSave(formData);
		}
	};

	if (!productType) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "100%",
					color: "text.secondary",
				}}
			>
				<Typography>Select a product type to view details</Typography>
			</Box>
		);
	}

	if (!formData) return null;

	return (
		<Box sx={{ p: 1.5, height: "100%", overflow: "auto" }}>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					mb: 1,
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
						{isNewProductType ? "New Product Type" : formData.Name}
					</Typography>
					{isSaving && <CircularProgress size={16} />}
				</Box>
				<Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
					{isNewProductType && (
						<Button
							color="primary"
							variant="contained"
							size="small"
							onClick={handleCreate}
							disabled={!canCreate || isSaving}
						>
							{isSaving ? "CREATING..." : "CREATE"}
						</Button>
					)}
					<Tooltip title="Close">
						<IconButton size="small" onClick={onClose} disabled={isSaving}>
							<CloseIcon />
						</IconButton>
					</Tooltip>
				</Box>
			</Box>

			<Divider sx={{ mb: 1.5 }} />

			<Grid2 container spacing={1.5}>
				<Grid2 size={{ xs: 12 }}>
					<Typography
						sx={{
							color: "primary.main",
							fontWeight: 600,
							fontSize: "0.85rem",
							mb: 0.5,
						}}
					>
						Basic Information
					</Typography>
				</Grid2>

				<Grid2 size={{ xs: 12, md: 6 }}>
					<TextField
						label="Name"
						value={formData.Name}
						onChange={(e) => handleChange("Name", e.target.value)}
						onBlur={(e) => handleBlurSave("Name", e.target.value)}
						fullWidth
						size="small"
						required
						error={touched.Name && !!errors.Name}
						helperText={touched.Name && errors.Name}
						slotProps={{ inputLabel: { shrink: true } }}
					/>
				</Grid2>

				<Grid2 size={{ xs: 12, md: 6 }}>
					<TextField
						label="Prefix"
						value={formData.Prefix}
						onChange={(e) => handleChange("Prefix", e.target.value)}
						onBlur={(e) => handleBlurSave("Prefix", e.target.value)}
						fullWidth
						size="small"
						slotProps={{
							inputLabel: { shrink: true },
							htmlInput: { maxLength: 2 },
						}}
					/>
				</Grid2>

				<Grid2 size={{ xs: 12 }}>
					<TextField
						label="Description"
						value={formData.Description ?? ""}
						onChange={(e) => handleChange("Description", e.target.value)}
						onBlur={(e) => handleBlurSave("Description", e.target.value)}
						fullWidth
						size="small"
						multiline
						rows={2}
						slotProps={{ inputLabel: { shrink: true } }}
					/>
				</Grid2>

				<Grid2 size={{ xs: 12, md: 6 }}>
					<TextField
						label="TPH Goal"
						type="number"
						value={formData.TPHGoal ?? ""}
						onChange={(e) =>
							handleChange(
								"TPHGoal",
								e.target.value === ""
									? null
									: Number.parseFloat(e.target.value),
							)
						}
						onBlur={(e) => handleNumericBlurSave("TPHGoal", e.target.value)}
						fullWidth
						size="small"
						slotProps={{
							inputLabel: { shrink: true },
							htmlInput: { step: 0.01 },
						}}
					/>
				</Grid2>

				<Grid2 size={{ xs: 12 }}>
					<FormControlLabel
						control={
							<Checkbox
								checked={formData.Active}
								onChange={(e) =>
									handleCheckboxChange("Active", e.target.checked)
								}
								size="small"
							/>
						}
						label="Active"
						sx={{ ml: 0 }}
					/>
				</Grid2>
			</Grid2>
		</Box>
	);
};
